import axios from "axios";
import { DropDownOption } from "../models/primereact";
import { BusinessHomeProfile } from "../models/businessHomeProfile";
import {
  ROLE_TYPE_BUSINESS_VENDOR,
  ROLE_TYPE_CAREGIVER,
  ROLE_TYPE_FAMILY_MEMBER,
  ROLE_TYPE_FACILITY_WORKER,
  ROLE_TYPE_MHP,
  ROLE_TYPE_NURSE,
  ROLE_TYPE_PROVIDER,
} from "../constants/RegisterConstants";
import { RouterAddress } from "../constants/RouterAddress";
import QRCode from "qrcode";
import { normalizePlainText, sanitizeWebsiteSlug } from "./securityUtils";

export async function getQrCodeDataUrl(url: string): Promise<string> {
  try {
    return await QRCode.toDataURL(url);
  } catch (err) {
    console.error("Failed to generate QR code data URL");
    throw err;
  }
}
export function capitalizeFirstLetter(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function phoneNumberFormatter(phoneNumber: string | undefined): string {
  if (!phoneNumber) return "N/A";
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
}

export function calculateAge(birthDateString) {
  const today = new Date();
  const birthDate = new Date(birthDateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // Adjust age if the current month is before the birth month,
  // or if it's the same month but the current day is before the birth day
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

export function calculateDeepAge(birthDate) {
  const today = new Date();
  const dob = new Date(birthDate);

  let years = today.getFullYear() - dob.getFullYear();
  let months = today.getMonth() - dob.getMonth();
  let days = today.getDate() - dob.getDate();

  // Adjust years and months if the current date is before the birth date in the current year/month
  if (months < 0 || (months === 0 && days < 0)) {
    years--;
    months = (months + 12) % 12; // Add 12 to the negative month difference
  }

  // Adjust days if the current day is before the birth day in the current month
  if (days < 0) {
    // Go back one month, and get the number of days in that month
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
    months--; // Adjust the month count down by one

    // If adjusting days caused months to become negative, adjust years
    if (months < 0) {
      months = 11;
      // years--;
    }
  }

  return `${years} years, ${months} months, and ${days} days`;
}

export function showPluralIfNeeded(size: number, text: string): string {
  if (size <= 1) {
    return text;
  }
  return `${text}s`;
}

export const convertIsoToDate = (isoString: string | undefined): string => {
  return new Date(isoString!).toLocaleString("en-US").toString();
};

export const convertIsoToDateOnly = (isoString: string | undefined): string => {
  if (!isoString) return "N/A";
  return new Date(isoString!).toLocaleDateString("en-US").toString();
};

export function sortObjectByKey(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  const sortedEntries = Object.entries(obj).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  return Object.fromEntries(sortedEntries);
}

export const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export function extractCoordinates(
  jsonString: string | undefined,
): { longitude: number; latitude: number } | null {
  if (!jsonString) return null;
  try {
    const parsedData = JSON.parse(jsonString);
    if (
      parsedData.type === "POINT" &&
      Array.isArray(parsedData.coordinates) &&
      parsedData.coordinates.length === 2
    ) {
      const [longitude, latitude] = parsedData.coordinates;
      return { longitude, latitude };
    }
    return null;
  } catch (error) {
    console.error("Invalid JSON string");
    return null;
  }
}

interface LocationDetails {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  route: string;
  street_number: string;
  subLocality: string;
}

export const extractLocationDetails = (placeData: any): LocationDetails => {
  let addressLine1 = "";
  let addressLine2 = "";
  let city = "";
  let state = "";
  let country = "";
  let postalCode = "";
  let street_number = "";
  let route = "";
  let locality = "";
  let subLocality = "";
  let sublocality_level_1 = "";
  let sublocality_level_2 = "";
  let premise = "";

  placeData?.address_components?.forEach((component) => {
    if (component?.types?.includes("administrative_area_level_3")) {
      city = component?.short_name;
    }
    if (component?.types?.includes("locality")) {
      locality = component?.short_name;
    }
    if (component?.types?.includes("locality")) {
      locality = component?.short_name;
    }

    if (!city) {
      city = locality;
    }

    if (
      component?.types?.includes("premise") ||
      component?.types?.includes("subpremise")
    ) {
      premise = component?.short_name;
    }
    if (component?.types?.includes("sublocality_level_1")) {
      subLocality = component?.short_name;
    }
    if (component?.types?.includes("sublocality_level_2")) {
      sublocality_level_2 = component?.short_name;
    }
    if (component?.types?.includes("route")) {
      route = component?.short_name;
    }
    if (component?.types?.includes("street_number")) {
      street_number = component?.long_name;
    }
    if (component?.types?.includes("administrative_area_level_1")) {
      state = component?.short_name;
    }
    if (component?.types?.includes("country")) {
      country = component?.short_name;
    }
    if (component?.types?.includes("postal_code")) {
      postalCode = component?.short_name;
    }
  });

  addressLine2 = [
    premise,
    sublocality_level_2,
    street_number,
    sublocality_level_1,
    route,
    subLocality,
    locality,
  ]
    .filter(Boolean)
    .join(", ");

  addressLine1 = [
    street_number,
    route,
    premise,
    city,
    state,
    country,
    postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return {
    city,
    state,
    country,
    postalCode,
    addressLine2,
    route,
    street_number,
    subLocality,
    addressLine1,
  };
};

export const getOptionNameListFromOptions = (options: DropDownOption[] | undefined): string => {
  if (!options || options.length === 0) {
    return "N/A";
  }
  return options.map(option => option.name).join(", ");
}

export function getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
}

export async function getAddressFromCoordinates(latitude: number, longitude: number): Promise<string | undefined> {
  const response = await axios.get(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY_GEOCODE}`,
  );
  if (response.status === 200) {
    const address = response.data.results[0]?.formatted_address || "Current Location";
    return address;
  }

  console.error("Error fetching address from coordinates");
  return undefined;
}

export const getWebsiteName = (businessName: string): string => {
  return sanitizeWebsiteSlug(businessName);
}

export function getWebsiteUrlForHome(home: BusinessHomeProfile): string {
  const baseUrl = window.location.origin;
  const websiteName = sanitizeWebsiteSlug(home.websiteUniqueName || home.businessName || "");
  const statePath = encodeURIComponent(normalizePlainText(home.state || "", { maxLength: 20 }));
  return `${baseUrl}/site/${statePath}/${websiteName}`;
}

export function convertListToDropDownOptions(list: string[] | undefined): DropDownOption[] {
  if (!list) return [];
  const options = list.map((item) => ({ name: item, code: item }));
  return options;
}

export function convertEpochToDateString(epochTime: number | undefined): string {
  if (!epochTime) return "";
  const date = new Date(0);
  date.setUTCSeconds(epochTime);
  return date.toLocaleDateString("en-US");
}

export const redirectToHome = (userRoleType: string) => {
  let homePagePath;
  if (userRoleType === ROLE_TYPE_PROVIDER) {
    homePagePath = RouterAddress.PROVIDER_HOME;
  } else if (userRoleType === ROLE_TYPE_NURSE) {
    homePagePath = RouterAddress.NURSE_HOME;
  } else if (userRoleType === ROLE_TYPE_CAREGIVER) {
    homePagePath = RouterAddress.CAREGIVER_HOME;
  } else if (userRoleType === ROLE_TYPE_MHP) {
    homePagePath = RouterAddress.MHP_HOME;
  } else if (userRoleType === ROLE_TYPE_FAMILY_MEMBER) {
    homePagePath = RouterAddress.FAMILY_MEMBER_HOME;
  } else if (userRoleType === ROLE_TYPE_BUSINESS_VENDOR) {
    homePagePath = RouterAddress.VENDOR_HOME;
  } else if (userRoleType === ROLE_TYPE_FACILITY_WORKER) {
    homePagePath = RouterAddress.FACILITY_WORKER_HOME;
  } else {
    homePagePath = RouterAddress.APP_ROOT;
  }

  return homePagePath;
};

export const redirectToHomeForAuthMode = (
  authMode: string,
  userRoleType: string,
) => {
  if (authMode === "multi_role_context") {
    if (!String(userRoleType || "").trim()) {
      return RouterAddress.PROVIDER_HOME;
    }
    if (userRoleType === ROLE_TYPE_PROVIDER) {
      return RouterAddress.PROVIDER_HOME;
    }
    if (userRoleType === ROLE_TYPE_CAREGIVER) {
      return RouterAddress.CAREGIVER_HOME;
    }
    if (userRoleType === ROLE_TYPE_NURSE) {
      return RouterAddress.NURSE_HOME;
    }
    if (userRoleType === ROLE_TYPE_MHP) {
      return RouterAddress.MHP_HOME;
    }
    if (userRoleType === ROLE_TYPE_FAMILY_MEMBER) {
      return RouterAddress.FAMILY_MEMBER_HOME;
    }
    if (userRoleType === ROLE_TYPE_FACILITY_WORKER) {
      return RouterAddress.FACILITY_WORKER_HOME;
    }
    return redirectToHome(userRoleType);
  }

  return redirectToHome(userRoleType);
};

export const getStatusNameForDisplay = (status: string): string => {
  return status.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

export const withinLast11Months = (dateString: string | undefined): boolean => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  const elevenMonthsAgo = new Date();
  elevenMonthsAgo.setMonth(now.getMonth() - 11);
  return date >= elevenMonthsAgo && date <= now;
}
